import * as React from "react";
import { cva } from "class-variance-authority";
import type { VariantProps } from "class-variance-authority";
import {
  CheckCircle,
  XCircle,
  AlertTriangle,
  Info,
  X,
} from "lucide-react";

import { cn } from "@/lib/utils";

/* ─── Toast Variants ─── */

const toastVariants = cva(
  "pointer-events-auto relative flex w-full items-start gap-3 overflow-hidden rounded-lg border p-4 shadow-lg transition-all data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:slide-out-to-right-full data-[state=open]:slide-in-from-right-full",
  {
    variants: {
      variant: {
        success: "border-success/30 bg-success-bg text-success",
        error: "border-error/30 bg-error-bg text-error",
        warning: "border-warning/30 bg-warning-bg text-warning",
        info: "border-brand-200 bg-brand-50 text-brand-800",
      },
    },
    defaultVariants: {
      variant: "info",
    },
  }
);

/* ─── Types ─── */

type ToastVariant = NonNullable<VariantProps<typeof toastVariants>["variant"]>;

interface Toast {
  id: string;
  variant: ToastVariant;
  title: string;
  description?: string;
  duration?: number;
}

type ToastAction =
  | { type: "ADD"; toast: Toast }
  | { type: "REMOVE"; id: string };

/* ─── State Management ─── */

const TOAST_DEFAULT_DURATION = 5000;
const TOAST_MAX_VISIBLE = 5;

let toastCount = 0;

function generateId(): string {
  toastCount += 1;
  return `toast-${toastCount}-${Date.now()}`;
}

function toastReducer(state: Toast[], action: ToastAction): Toast[] {
  switch (action.type) {
    case "ADD":
      return [action.toast, ...state].slice(0, TOAST_MAX_VISIBLE);
    case "REMOVE":
      return state.filter((t) => t.id !== action.id);
    default:
      return state;
  }
}

/* ─── Context ─── */

interface ToastContextValue {
  toasts: Toast[];
  addToast: (toast: Omit<Toast, "id">) => string;
  removeToast: (id: string) => void;
}

const ToastContext = React.createContext<ToastContextValue | undefined>(
  undefined
);

/* ─── Imperative bridge ───
 * Lets non-React code (e.g. the global React Query MutationCache.onError safety
 * net in App.tsx) raise a toast. ToastProvider registers its addToast here on
 * mount; notifyToast is a no-op before the provider mounts. */
let externalAddToast: ((toast: Omit<Toast, "id">) => void) | null = null;

export function notifyToast(toast: Omit<Toast, "id">): void {
  externalAddToast?.(toast);
}

/* ─── Icon Map ─── */

const iconMap: Record<ToastVariant, React.ElementType> = {
  success: CheckCircle,
  error: XCircle,
  warning: AlertTriangle,
  info: Info,
};

/* ─── Toast Item ─── */

interface ToastItemProps extends Toast {
  onDismiss: (id: string) => void;
}

function ToastItem({
  id,
  variant,
  title,
  description,
  duration = TOAST_DEFAULT_DURATION,
  onDismiss,
}: ToastItemProps) {
  const [state, setState] = React.useState<"open" | "closed">("open");
  const Icon = iconMap[variant];

  React.useEffect(() => {
    if (duration <= 0) return;

    const timer = setTimeout(() => {
      setState("closed");
    }, duration);

    return () => clearTimeout(timer);
  }, [duration]);

  React.useEffect(() => {
    if (state === "closed") {
      const exitTimer = setTimeout(() => {
        onDismiss(id);
      }, 200);
      return () => clearTimeout(exitTimer);
    }
  }, [state, id, onDismiss]);

  return (
    <div
      role="alert"
      aria-live="assertive"
      data-state={state}
      className={cn(toastVariants({ variant }))}
    >
      <Icon className="h-5 w-5 shrink-0 mt-0.5" />
      <div className="flex-1 space-y-1">
        <p className="text-sm font-semibold leading-none">{title}</p>
        {description && (
          <p className="text-sm opacity-80">{description}</p>
        )}
      </div>
      <button
        type="button"
        onClick={() => setState("closed")}
        className="shrink-0 rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-border-focus"
      >
        <X className="h-4 w-4" />
        <span className="sr-only">Dismiss</span>
      </button>
    </div>
  );
}

/* ─── Toast Provider ─── */

interface ToastProviderProps {
  children: React.ReactNode;
}

function ToastProvider({ children }: ToastProviderProps) {
  const [toasts, dispatch] = React.useReducer(toastReducer, []);

  const addToast = React.useCallback((toast: Omit<Toast, "id">) => {
    const id = generateId();
    dispatch({ type: "ADD", toast: { ...toast, id } });
    return id;
  }, []);

  const removeToast = React.useCallback((id: string) => {
    dispatch({ type: "REMOVE", id });
  }, []);

  // Expose addToast to non-React callers (the global mutation-error safety net).
  React.useEffect(() => {
    externalAddToast = addToast;
    return () => {
      if (externalAddToast === addToast) externalAddToast = null;
    };
  }, [addToast]);

  const value = React.useMemo(
    () => ({ toasts, addToast, removeToast }),
    [toasts, addToast, removeToast]
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        role="status"
        aria-label="Notifications"
        className="fixed top-4 right-4 z-[100] flex max-h-screen w-full max-w-sm flex-col gap-2 pointer-events-none"
      >
        {toasts.map((toast) => (
          <ToastItem key={toast.id} {...toast} onDismiss={removeToast} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}
ToastProvider.displayName = "ToastProvider";

/* ─── useToast Hook ─── */

function useToast() {
  const context = React.useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within a <ToastProvider>");
  }

  const { addToast, removeToast, toasts } = context;

  // Memoize the returned API. Without this, useToast() hands back a fresh
  // object — and fresh success/error/warning/info closures — on every
  // render. Any caller that puts the toast object in a useEffect/useMemo/
  // useCallback dependency array then sees it change every render, which
  // re-fires that hook every render. In AdHocReportBuilder that produced a
  // synchronous infinite render loop (effect → setState → render → effect).
  // addToast/removeToast are already stable (useCallback); only `toasts`
  // varies, so the memo recomputes solely when a toast is added/removed.
  return React.useMemo(
    () => ({
      toasts,
      toast: addToast,
      dismiss: removeToast,
      success: (title: string, description?: string) =>
        addToast({ variant: "success", title, description }),
      error: (title: string, description?: string) =>
        addToast({ variant: "error", title, description }),
      warning: (title: string, description?: string) =>
        addToast({ variant: "warning", title, description }),
      info: (title: string, description?: string) =>
        addToast({ variant: "info", title, description }),
    }),
    [toasts, addToast, removeToast],
  );
}

export { ToastProvider, useToast, toastVariants };
export type { Toast, ToastVariant };
